// PrimeReact SelectButton: onChange(e) => e.value (the selected option's value, typed as any).
// No existing usages in apps/web/src; shape confirmed from type definitions:
//   - apps/web/node_modules/primereact/selectbutton/selectbutton.d.ts
//       SelectButtonChangeEvent extends FormEvent => { value: Nullable<T> }
// SelectButton does NOT support `inputId` — it renders a <div>-based button group.
// Label association uses `ariaLabelledBy` prop instead of htmlFor.
// The label element is given an id; the SelectButton root div receives ariaLabelledBy.

import { useId, type JSX } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { SelectButton } from 'primereact/selectbutton'

interface SelectButtonOption {
  label: string
  value: string | number
}

interface Props {
  name: string
  label: string
  options: SelectButtonOption[]
  helpText?: string
  required?: boolean
  disabled?: boolean
  multiple?: boolean
}

export function FormSelectButton({
  name,
  label,
  options,
  helpText,
  required,
  disabled,
  multiple,
}: Props): JSX.Element {
  const {
    control,
    formState: { errors },
  } = useFormContext()
  const error = errors[name]
  const errorMessage = typeof error?.message === 'string' ? error.message : undefined
  const id = useId()
  const labelId = `${id}-label`
  const errorId = `${id}-error`
  const helpId = `${id}-help`
  const describedBy =
    [helpText && helpId, errorMessage && errorId].filter(Boolean).join(' ') || undefined

  return (
    <div className="flex flex-col gap-1 mb-3">
      {/* SelectButton has no inner <input>, so we use id+ariaLabelledBy instead of htmlFor+inputId */}
      <span id={labelId} className="text-sm font-medium">
        {label}
        {required && <span aria-hidden> *</span>}
      </span>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <SelectButton
            id={id}
            value={field.value}
            onChange={(e) => field.onChange(e.value)}
            onBlur={field.onBlur}
            options={options}
            disabled={disabled}
            multiple={multiple}
            ariaLabelledBy={labelId}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={describedBy}
          />
        )}
      />
      {helpText && (
        <small id={helpId} className="text-gray-500">
          {helpText}
        </small>
      )}
      {errorMessage && (
        <small id={errorId} role="alert" className="text-red-600">
          {errorMessage}
        </small>
      )}
    </div>
  )
}
